import React, { useState, useEffect } from 'react'
import { Classes, EditableText, Intent } from '@blueprintjs/core'
import { NavigateFn, WindowLocation } from '@reach/router'
import {
  IconButton,
  Confirmation,
  Flex,
  Toaster,
  ColorSelect,
  EmployeeWithDetailsPopover,
  PrettyDate,
  Avatar,
  Dialog,
} from '@ui'
import api, { IWorkplaceGroup, FormErrors } from '@api'
import useWorkplaceGroup, { UseWorkplaceGroupHook } from './hooks/useWorkplaceGroup'
import { useReload } from '@hooks/reloadContext'
import EmployeesManageSection from './EmployeesManageSection'
import SpacesManageSection from './SpacesManageSection'
import { ApolloError } from 'apollo-boost'
import track from '@track'
import Event from '@track/Event'
import classnames from 'classnames'

interface WorkplaceGroupDetailsDialogProps {
  workplaceGroupId: string
  floorId: string
  navigate: NavigateFn
  location: WindowLocation
}

export interface FormWorkplaceGroup {
  name: string
}

type WorkplaceGroupFormErrors = FormErrors<FormWorkplaceGroup>

interface DialogToolbarProps {
  onClose?: () => void
  onDelete?: () => Promise<IWorkplaceGroup | void>
}

export default function WorkplaceGroupDetailsDialog({
  workplaceGroupId,
  navigate,
}: WorkplaceGroupDetailsDialogProps) {
  const workplaceGroup = useWorkplaceGroup(workplaceGroupId)
  const onClose = () => navigate && navigate(window.location.pathname)

  const toolbarProps: DialogToolbarProps = {}
  const reload = useReload()

  toolbarProps.onClose = onClose

  if (workplaceGroupId) {
    toolbarProps.onDelete = () => {
      return api.workplaceGroup.remove(workplaceGroupId).then(() => reload.workplaceGroups())
    }
  }

  return (
    <Dialog
      isOpen
      usePortal
      style={{ width: 900, position: 'relative' }}
      canEscapeKeyClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      onClose={onClose}
      toolbar={<DialogToolbar {...toolbarProps} />}
      body={<Details workplaceGroup={workplaceGroup} />}
    ></Dialog>
  )
}

function Skeleton() {
  const placeholder = <span className={Classes.SKELETON}>Loooooong placeholder text</span>

  return (
    <Flex align="center">
      <Avatar skeleton size={Avatar.Size.XS} />
      <Flex direction="column">
        <span style={{ padding: 10, fontSize: 18 }}>{placeholder}</span>
      </Flex>
    </Flex>
  )
}

function Details({ workplaceGroup }: { workplaceGroup: UseWorkplaceGroupHook }) {
  const [input, setInput] = useState('')
  const [errors, setErrors] = useState<WorkplaceGroupFormErrors>({})
  const [color, setColor] = useState('')

  const reload = useReload()

  useEffect(() => {
    setInput(workplaceGroup.item?.name ?? '')
    setColor(workplaceGroup.item?.color || '')
  }, [workplaceGroup.item])

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      name: '',
    }))
  }, [input])

  const handleErrors = ({ kind, graphQLErrors }: any) => {
    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: WorkplaceGroupFormErrors = graphQLErrors.reduce(
        (result: WorkplaceGroupFormErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const pushColor: (color: string, enableUndo?: boolean) => Promise<any> = (
    color: string,
    enableUndo = true
  ) => {
    if (!workplaceGroup.item) return new Promise(() => undefined)
    const oldColor = workplaceGroup.item.color

    track.workplaceGroupDialog({
      eventName: Event.INLINE_ACTION,
      props: {
        actionName: 'Change Color',
        actionValue: null,
      },
    })

    return api.workplaceGroup
      .updateColor({
        workplaceGroupId: workplaceGroup.item.id,
        color,
      })
      .then(() => {
        setColor(color)
        if (!enableUndo) return
        Toaster.success({
          message: 'Workplace group color changed',
          action: {
            onClick: () => {
              pushColor(oldColor, false)
              track.workplaceGroupDialog({
                eventName: Event.UNDO_CLICK,
                props: {
                  component: 'Color picker',
                  actionName: 'Undo Color Change',
                },
              })
            },
            text: 'Undo',
          },
        })
      })
      .then(() => reload.workplaceGroups())
      .catch((e) =>
        Toaster.fail({
          message: e.message,
        })
      )
  }

  return (
    <div className={Classes.DIALOG_BODY}>
      <div className="mb-6 flex items-center text-grey-600">
        {!workplaceGroup.item && <Skeleton />}
        {workplaceGroup.item?.createdBy && (
          <>
            <Avatar
              name={workplaceGroup.item.createdBy.employee.name}
              assets={workplaceGroup.item.createdBy.employee.assets}
              size={Avatar.Size.XS}
            />
            <EmployeeWithDetailsPopover
              buttonProps={{ small: true, minimal: true }}
              employee={workplaceGroup.item.createdBy.employee}
            />
          </>
        )}
        {workplaceGroup.item && (
          <>
            <span style={{ paddingRight: '0.5em' }}>created on</span>
            <PrettyDate
              value={workplaceGroup.item?.createdAt}
              timeStringFormat={'YYYY-MM-DD hh:mm:ss'}
            />
          </>
        )}
      </div>
      <div className="flex items-center">
        {workplaceGroup.item && (
          <ColorSelect
            value={color}
            onChange={pushColor}
            disabled={!workplaceGroup.item}
            buttonProps={{ minimal: true }}
          />
        )}

        <div
          className={classnames('ml-1 text-2xl', {
            [Classes.SKELETON]: !workplaceGroup.item,
          })}
          style={{ width: '90%' }}
        >
          <EditableText
            multiline={true}
            maxLines={3}
            intent={errors.name ? Intent.DANGER : undefined}
            value={input}
            className={!workplaceGroup.item ? Classes.SKELETON : ''}
            onChange={setInput}
            onConfirm={() => {
              if (workplaceGroup.item) {
                if (input !== workplaceGroup.item.name) {
                  api.workplaceGroup
                    .updateName({ id: workplaceGroup.item.id, name: input })
                    .then(() =>
                      Toaster.success({
                        message: 'Workplace group name changed',
                      })
                    )
                    .then(() => reload.workplaceGroups())
                    .catch((error: ApolloError) => {
                      handleErrors(error)
                      Toaster.fail({
                        error,
                      })
                    })

                  track.workplaceGroupDialog({
                    eventName: Event.INLINE_ACTION,
                    props: {
                      actionName: 'Rename Workplace Group',
                      actionValue: null,
                    },
                  })
                }
              }
            }}
          />
        </div>
      </div>
      {errors.name && (
        <div data-cy="name_error" style={{ color: 'red', marginBottom: 5 }}>
          {errors.name}
        </div>
      )}
      <EmployeesManageSection workplaceGroup={workplaceGroup} />
      <SpacesManageSection workplaceGroup={workplaceGroup} />
    </div>
  )
}

function DialogToolbar({ onDelete, onClose }: DialogToolbarProps) {
  const [disableConfirmation] = useState(false)

  const onConfirm = () => {
    onClose?.()
    onDelete?.()

    track.workplaceGroupDialog({
      eventName: Event.DELETE_RECORD_DIALOG,
    })

    Toaster.success({
      message: 'Workplace Group deleted',
    })
  }

  return (
    <div className="dialog-buttons">
      {onDelete && (
        <Confirmation
          shouldHide={disableConfirmation}
          enabled={!disableConfirmation}
          text="Delete this workplace group?"
          onConfirm={onConfirm}
        >
          <IconButton
            minimal
            data-button="delete_workplace_group"
            tooltip="Delete"
            iconName="trash"
          />
        </Confirmation>
      )}
      {onClose && <IconButton minimal tooltip="Close" iconName="times" onClick={onClose} />}
    </div>
  )
}
